import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { GrantRowTable } from "permissions/_views/grant-row/grant-row";
import type { RecordKindCount } from "permissions/_views/people/person-record-grants-model";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import { type ReactNode, useId, useState } from "react";

/**
 * One area of the person page: a header with the area's stripe, icon and counts, the held rows, one
 * count row per per-record kind the person holds, and the kinds not granted folded under "Not granted
 * (n)".
 *
 * The header sticks under the person header at md and up, so while a long area scrolls the reader
 * still knows which one it is. Below md the person header scrolls away and only the current area's
 * header stays, at the top. The colour is a stripe and an icon, never a tinted card.
 */

export function areaAnchorId(area: string): string {
  return `area-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

const SectionRoot = styled("section")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  // Keeps an anchor jump from landing under the sticky headers.
  scrollMarginTop: "calc(var(--person-header-height, 0px) + 8px)",
}));

const Header = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.25),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  [theme.breakpoints.up("md")]: {
    top: "var(--person-header-height, 0px)",
  },
}));

const Stripe = styled("span")({
  position: "absolute",
  left: -1,
  top: -1,
  bottom: -1,
  width: 4,
  borderTopLeftRadius: 4,
});

/** What an area section draws: its rows, and the ones folded under the disclosure. */
type AreaRows<TRow> = Readonly<{
  area: string;
  granted: readonly TRow[];
  notGranted: readonly TRow[];
  /** The person page's per-record kinds, drawn after the held rows. */
  records?: readonly RecordKindCount[];
}>;

/**
 * Generic over the row so the templates page draws its areas the same way: its folded rows are the
 * kinds a template leaves out, and its counts say what it grants and takes away.
 */
export default function PersonAreaSection<TRow>(
  props: Readonly<{
    section: AreaRows<TRow>;
    /** The header's counts: `areaCounts(section)` on the person page, the template's on the templates page. */
    counts: string;
    /** The disclosure's words, before the count. Defaults to "Not granted". */
    foldedLabel?: string;
    /** Opens the not-granted rows from the start, as the Not granted filter does. */
    expandNotGranted?: boolean;
    renderRow: (row: TRow) => ReactNode;
    /** A per-record kind's count row, and its list once shown. The person page draws these. */
    renderRecords?: (entry: RecordKindCount) => ReactNode;
  }>,
) {
  const { section, counts, expandNotGranted, renderRow, renderRecords } = props;
  const foldedLabel = props.foldedLabel ?? "Not granted";
  const token = permissionAreaToken(section.area);
  const headingId = useId();
  const notGrantedId = useId();
  const [open, setOpen] = useState(false);
  const showNotGranted = expandNotGranted || open;

  return (
    <SectionRoot id={areaAnchorId(section.area)} aria-labelledby={headingId}>
      <Header>
        <Stripe sx={{ backgroundColor: token.color }} aria-hidden />
        <token.Icon fontSize="small" sx={{ color: token.color }} aria-hidden />
        <Typography id={headingId} component="h2" variant="subtitle1" sx={{ fontWeight: 500, flex: "1 1 auto" }}>
          {section.area}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {counts}
        </Typography>
      </Header>
      {section.granted.length > 0 && (
        <GrantRowTable label={`${section.area}: granted`}>{section.granted.map(renderRow)}</GrantRowTable>
      )}
      {renderRecords && section.records?.map((entry) => <Box key={entry.kind.slug}>{renderRecords(entry)}</Box>)}
      {section.notGranted.length > 0 && (
        <Box sx={section.granted.length > 0 ? { borderTop: 1, borderColor: "divider", borderTopStyle: "dashed" } : undefined}>
          {!expandNotGranted && (
            <Button
              color="inherit"
              size="small"
              aria-expanded={showNotGranted}
              aria-controls={notGrantedId}
              onClick={() => setOpen(!open)}
              startIcon={
                <ChevronRightIcon sx={{ transform: showNotGranted ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />
              }
              sx={{ m: 0.5, textTransform: "none", color: "text.secondary", minHeight: 44 }}
            >
              {foldedLabel} ({section.notGranted.length})
            </Button>
          )}
          <Box id={notGrantedId} hidden={!showNotGranted}>
            {showNotGranted && (
              <GrantRowTable label={`${section.area}: not granted`}>{section.notGranted.map(renderRow)}</GrantRowTable>
            )}
          </Box>
        </Box>
      )}
    </SectionRoot>
  );
}
